/* Line-level delta between a questionnaire answer's stored HTML and the AI's
 * proposed replacement.
 *
 * `proposeQuestionnaireAnswers` has no targeted operation vocabulary — it always
 * carries the COMPLETE new answer, because accepting overwrites the field. Shown
 * literally that makes every proposal look like a full rewrite, even when the
 * model only filled one label. Aligning the two versions line by line recovers
 * what actually changed, so a proposal renders the way an editor suggestion
 * does: the untouched text plain, the new text highlighted.
 *
 * Lines, not blocks: these answers are label scaffolds (`TUG: <br>STEADI: `),
 * so a block-level comparison would mark a whole paragraph changed when one
 * label was filled. */

import { toAnswerLines as toSharedAnswerLines } from '@seen/core/models/care-plans/answerScaffold'
import { parseAnswerHtml } from '#src/components/care-plan/parseAnswerHtml.ts'

export type AnswerSegmentKind = 'unchanged' | 'added' | 'removed'

export interface AnswerSegment {
  kind: AnswerSegmentKind
  // Inline HTML, rendered as its own row.
  html: string
}

/* The split itself is the shared model's (`@seen/core`'s `answerScaffold`): a
 * line index addresses the same line here, in a write, and in the list of
 * labels the AI read tool enumerates for the model. */
export function toAnswerLines(html: string): string[] {
  return toSharedAnswerLines(html, parseAnswerHtml)
}

const CONTAINS_MARKUP = /<[a-z]/i

/* One changed line against its counterpart. Filling a label leaves the label
 * itself intact, so the retained head is emitted unchanged and only the value
 * is highlighted — the difference between a green word and a green paragraph.
 *
 * Restricted to a plain-text head so the split stays on an HTML boundary.
 * Matching on rendered text and then slicing the markup would cut mid-tag as
 * soon as a label carried any, producing HTML that no longer parses. */
function alignLine(before: string, after: string): AnswerSegment[] {
  /* An untouched line inside a changed run. Only the run's ENDS are trimmed by
   * the common prefix and suffix, so an unchanged line sitting between two
   * changed ones still arrives here — a section heading between two filled
   * labels, say — and would otherwise be struck through and re-added
   * identically. */
  if (before === after) {
    return [{ kind: 'unchanged', html: before }]
  }
  if (
    before.trim() &&
    !CONTAINS_MARKUP.test(before) &&
    after.startsWith(before)
  ) {
    return [
      { kind: 'unchanged', html: before },
      { kind: 'added', html: after.slice(before.length) },
    ]
  }
  const segments: AnswerSegment[] = []
  if (before.trim()) {
    segments.push({ kind: 'removed', html: before })
  }
  if (after.trim()) {
    segments.push({ kind: 'added', html: after })
  }
  return segments
}

/* What a proposal adds after a line's current value, when it keeps that value
 * verbatim as its head and writes nothing else; null when it does anything
 * other than append. The head must be plain text, for the reason `alignLine`
 * gives: a split on rendered text would cut the markup mid-tag. */
export function appendedHtml(
  currentHtml: string,
  proposedHtml: string
): string | null {
  if (
    !currentHtml.trim() ||
    CONTAINS_MARKUP.test(currentHtml) ||
    !proposedHtml.startsWith(currentHtml)
  ) {
    return null
  }
  const added = proposedHtml.slice(currentHtml.length)
  return added.trim() ? added : null
}

export function diffAnswerHtml(
  currentHtml: string,
  proposedHtml: string
): AnswerSegment[] {
  const before = toAnswerLines(currentHtml)
  const after = toAnswerLines(proposedHtml)

  let head = 0
  while (
    head < before.length &&
    head < after.length &&
    before[head] === after[head]
  ) {
    head += 1
  }
  let tail = 0
  while (
    tail < before.length - head &&
    tail < after.length - head &&
    before[before.length - 1 - tail] === after[after.length - 1 - tail]
  ) {
    tail += 1
  }

  const changedBefore = before.slice(head, before.length - tail)
  const changedAfter = after.slice(head, after.length - tail)

  const segments: AnswerSegment[] = before
    .slice(0, head)
    .map((html) => ({ kind: 'unchanged' as const, html }))

  /* Equal counts means the scaffold survived and each line maps to its own
   * counterpart, so each can be aligned on its own. Unequal counts means lines
   * were added or dropped and there is no honest pairing — show the removal and
   * the replacement whole rather than inventing an alignment. */
  if (changedBefore.length === changedAfter.length) {
    for (const [index, line] of changedBefore.entries()) {
      segments.push(...alignLine(line, changedAfter[index] ?? ''))
    }
  } else {
    for (const html of changedBefore) {
      if (html.trim()) {
        segments.push({ kind: 'removed', html })
      }
    }
    for (const html of changedAfter) {
      if (html.trim()) {
        segments.push({ kind: 'added', html })
      }
    }
  }

  for (const html of before.slice(before.length - tail)) {
    segments.push({ kind: 'unchanged', html })
  }
  return segments
}
